/**
 * The Patient's Medplum creation instant, stamped once (BAR-2787).
 *
 * Medplum records no creation date on a resource. Before this, the only exact
 * source was the oldest history version, which costs a request — and ~10 of a
 * user's 50k-point Medplum minute — per patient (BAR-2780).
 */

import { Bundle, Patient } from '@medplum/fhirtypes';
import { PATIENT_CREATED_AT_EXTENSION } from './extensions';

/** The stamped creation instant, if the Patient carries one. */
export function readPatientCreatedAt(patient: Pick<Patient, 'extension'>): string | undefined {
  return patient.extension?.find((extension) => extension.url === PATIENT_CREATED_AT_EXTENSION)?.valueDateTime;
}

/**
 * A copy of the Patient carrying `instant`, or the Patient unchanged if it is
 * already stamped — the stamp is never overwritten, since the first write is
 * the one closest to the real creation.
 */
export function withPatientCreatedAt(patient: Patient, instant: string): Patient {
  if (readPatientCreatedAt(patient)) {
    return patient;
  }

  return {
    ...patient,
    extension: [...(patient.extension ?? []), { url: PATIENT_CREATED_AT_EXTENSION, valueDateTime: instant }],
  };
}

/**
 * The creation instant from a history page: the oldest entry's `lastUpdated`.
 *
 * Medplum returns history newest-first, so the oldest is the LAST entry — but
 * only on the last page. Returns undefined when the bundle has a `next` link, so
 * a caller that read a partial history pages on instead of stamping a later
 * version's date as the creation.
 */
export function creationInstantFromHistory(history: Bundle<Patient>): string | undefined {
  if (history.link?.some((link) => link.relation === 'next')) {
    return undefined;
  }

  const entries = history.entry ?? [];
  return entries[entries.length - 1]?.resource?.meta?.lastUpdated;
}
